/**
 * Local-only reducer for the SplitPay screen — no global context, no
 * persistence. State lives only while the screen is mounted. Wraps a
 * `Map<lineId, sharerId[]>` plus the chosen payment method.
 *
 * Tap rules (from spec §3.4):
 *   1. Tap an unclaimed line → you alone claim it.
 *   2. Tap your own claim with no other sharers → unclaim (back to dotted).
 *   3. Tap a line where you're one of N sharers → drop yourself (N-1 left).
 *   4. Tap a line another participant claimed → join as second sharer.
 *   5. The "Split…" sheet path explicitly sets the sharer list (overrides taps).
 */
import { useReducer } from "react";

import type { GroupCartParticipant } from "@/api/group-cart";

export type SharerId = string;

export type SplitMethod = "card" | "cash";

export interface SplitState {
  /** lineId → ordered list of sharer guestIds (or "self" for the local guest). */
  sharers: Record<string, SharerId[]>;
  method: SplitMethod;
}

export type SplitAction =
  | { type: "toggle_self"; lineId: string; selfId: SharerId }
  | { type: "set_split"; lineId: string; sharers: SharerId[] }
  | { type: "set_method"; method: SplitMethod }
  | {
      type: "hydrate_from_attribution";
      orderLines: Array<{ lineId: string; addedByGuestId: string | null }>;
    }
  | { type: "reset" };

export const initialSplitState: SplitState = { sharers: {}, method: "card" };

export function splitReducer(state: SplitState, action: SplitAction): SplitState {
  switch (action.type) {
    case "toggle_self": {
      const cur = state.sharers[action.lineId] ?? [];
      const inIt = cur.includes(action.selfId);
      let next: SharerId[];
      if (cur.length === 0) {
        next = [action.selfId]; // unclaimed → you
      } else if (inIt && cur.length === 1) {
        next = []; // you alone → unclaim
      } else if (inIt) {
        next = cur.filter((s) => s !== action.selfId); // drop yourself
      } else {
        next = [...cur, action.selfId]; // join others
      }
      return { ...state, sharers: { ...state.sharers, [action.lineId]: next } };
    }
    case "set_split":
      return {
        ...state,
        sharers: { ...state.sharers, [action.lineId]: action.sharers },
      };
    case "set_method":
      return { ...state, method: action.method };
    case "hydrate_from_attribution": {
      const sharers: Record<string, SharerId[]> = {};
      for (const l of action.orderLines) {
        sharers[l.lineId] = l.addedByGuestId ? [l.addedByGuestId] : [];
      }
      return { ...state, sharers };
    }
    case "reset":
      return initialSplitState;
  }
}

/**
 * Build the ClaimedLine[] payload for paymentSplitApi.create from the
 * current state, restricted to lines where the local guest (`selfId`) is a
 * sharer. Each share is `1/sharers.length` of the line.
 */
export function deriveClaimedLines(
  state: SplitState,
  selfId: SharerId,
): Array<{ lineId: string; numerator: number; denominator: number }> {
  const out: Array<{ lineId: string; numerator: number; denominator: number }> = [];
  for (const [lineId, sharers] of Object.entries(state.sharers)) {
    if (sharers.includes(selfId)) {
      out.push({ lineId, numerator: 1, denominator: sharers.length });
    }
  }
  return out;
}

export interface ClaimSummary {
  myShare: number;
  claimedTotal: number;
  totalAmount: number;
  remainingForTable: number;
  uncoveredLineIds: string[];
}

/**
 * Reactive money summary. Caller passes order-line totals + the local guest
 * id; we map across the reducer state to compute (1) my share, (2) the sum
 * of all claimed shares (the table's progress towards strict coverage), and
 * (3) which lines still have nobody on them (used to inline-warn when a
 * card checkout is going to fail with OrderCoverageIncomplete).
 */
export function summarise(
  state: SplitState,
  selfId: SharerId,
  orderLines: Array<{ lineId: string; totalAmount: number }>,
): ClaimSummary {
  let myShare = 0;
  let claimedTotal = 0;
  let totalAmount = 0;
  const uncovered: string[] = [];
  for (const ln of orderLines) {
    totalAmount += ln.totalAmount;
    const sharers = state.sharers[ln.lineId] ?? [];
    if (sharers.length === 0) {
      uncovered.push(ln.lineId);
      continue;
    }
    const per = ln.totalAmount / sharers.length;
    claimedTotal += per * sharers.length; // == ln.totalAmount when fully claimed
    if (sharers.includes(selfId)) myShare += per;
  }
  return {
    myShare: round2(myShare),
    claimedTotal: round2(claimedTotal),
    totalAmount: round2(totalAmount),
    remainingForTable: round2(totalAmount - claimedTotal),
    uncoveredLineIds: uncovered,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Returns participants present on the cart sorted host-first, then by colour. */
export function rosterFromCart(
  participants: GroupCartParticipant[],
  hostGuestId: string | null,
): GroupCartParticipant[] {
  const sorted = [...participants];
  sorted.sort((a, b) => {
    if (a.guestId === hostGuestId) return -1;
    if (b.guestId === hostGuestId) return 1;
    return a.colorIndex - b.colorIndex;
  });
  return sorted;
}

export function useSplitPay() {
  return useReducer(splitReducer, initialSplitState);
}
